import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { ProgressBar } from 'components/common';

export type QuotaStatus = 'loading' | 'allowed' | 'blocked';

interface Props {
  onReady?: (status: QuotaStatus) => void;
  quota?: number;
  quotaThreshold?: number;
  style?: React.CSSProperties;
}

const AIToolsStatus = ({ onReady, quota, quotaThreshold, style }: Props) => {
  const {
    messages,
    serverConfig: { aiConfig },
    theme
  } = useReduxState();

  const [percentageConsumed, setPercentageConsumed] = useState(0);

  const { data, loading } = useApiData<{
    currentCost: number;
    success: boolean;
  }>({
    onlyIf: aiConfig?.enabled,
    url: '/admin/ai-tools/cost'
  });

  useEffect(() => {
    if (loading && onReady) onReady('loading');
    else if (data?.success) {
      const percentage = Math.min(
        data.currentCost / (quota || aiConfig.quota),
        1
      );
      if (onReady) onReady(percentage >= 1 ? 'blocked' : 'allowed');
      setPercentageConsumed(percentage);
    }
  }, [data, loading]);

  const color =
    percentageConsumed >= 1
      ? theme.dangerColor
      : percentageConsumed > (quotaThreshold || aiConfig.quotaThreshold)
      ? theme.warningStandaloneColor
      : theme.successStandaloneColor;

  return (
    <div className="ai-quota-consumed" style={style}>
      <ProgressBar
        animate={loading}
        color={color}
        label={getMessageWithPlaceholders({
          message: messages.aiToolsQuotaConsumed,
          placeholders: {
            percentageConsumed: loading
              ? '...'
              : `${(percentageConsumed * 100).toFixed(0)}%`
          }
        })}
        labelStyle={{ lineHeight: 1.2, paddingBottom: 2, textAlign: 'left' }}
        progress={percentageConsumed}
        progressAnimationContrast={0.5}
        progressStyle={{ borderRadius: 10, height: 8 }}
      />

      <style jsx>{`
        .ai-quota-consumed {
          padding: 10px 0;
          max-width: 320px;
        }
      `}</style>
    </div>
  );
};

export default AIToolsStatus;
